import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  getToken,
  getRefreshToken,
  getAccessTokenExpiryMs,
  logoutAndWipeBrowser,
} from '../Utils/authUtils';
import { refreshAccessToken } from '../Utils/refreshAccessToken';

// Refresh this long before the access token actually expires, so in-flight and
// about-to-fire requests always carry a valid token.
const SKEW_MS = 60_000;

/**
 * Keeps the clinician access token fresh while the app is open. Mounted once
 * near the router root. Because every authenticated thunk reads the token from
 * storage via getToken() at call time, silently rotating the stored token here
 * transparently keeps all of them authenticated — no per-request interceptor and
 * no changes to the ~69 existing fetch thunks.
 *
 * Behaviour:
 *  - On mount, if the access token is already expired/near-expiry but a refresh
 *    token exists, refresh immediately (covers returning-user / laptop-sleep).
 *  - Otherwise schedule a refresh SKEW_MS before the JWT `exp`, rescheduling
 *    after each success.
 *  - ALSO refresh when the tab becomes visible / regains focus / comes back
 *    online. Browsers throttle and eventually suspend setTimeout in background
 *    tabs, so a tab left open for hours/days never fires its scheduled refresh;
 *    by the time the user returns the access token is long dead and every
 *    request 401s (blank reports, "Data Source Connection Error"). These events
 *    refresh the instant the user comes back, before they can click.
 *  - If refresh fails (no/expired refresh token, 401), clear the session and
 *    send the user to /login — a clean re-login, never a broken screen.
 *
 * Renders nothing.
 */
export default function TokenRefreshManager() {
  const dispatch = useDispatch();
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let cancelled = false;

    const clearTimer = () => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    };

    // The browser no longer holds a PACS/DCM token (DICOMweb goes through the backend
    // proxy, authenticated by the app session). There is nothing separate to keep alive,
    // so only the app access token is refreshed below.

    const onFailure = () => {
      void logoutAndWipeBrowser(dispatch);
    };

    const runRefresh = async () => {
      if (cancelled || !getRefreshToken()) {
        return;
      }
      const newToken = await refreshAccessToken();
      if (cancelled) {
        return;
      }
      if (newToken) {
        schedule();
      } else {
        onFailure();
      }
    };

    function schedule() {
      if (cancelled) {
        return;
      }
      clearTimer();
      // Nothing to keep alive if the user isn't logged in / has no refresh token.
      if (!getToken() || !getRefreshToken()) {
        return;
      }
      const expMs = getAccessTokenExpiryMs();
      if (!expMs) {
        return;
      }
      const delay = Math.max(0, expMs - Date.now() - SKEW_MS);
      timer.current = setTimeout(runRefresh, delay);
    }

    // Refresh now, but only if the stored access token is actually expired or
    // within the skew window — so returning focus doesn't hammer the endpoint on
    // every tab switch. refreshAccessToken() is single-flight, so concurrent
    // triggers collapse into one request.
    const refreshIfStale = () => {
      if (cancelled || !getToken()) {
        return;
      }
      const exp = getAccessTokenExpiryMs();
      if (exp !== null && exp <= Date.now() && !getRefreshToken()) {
        // Older sessions may not have a refresh token. They remain valid until
        // their actual expiry, but cannot be renewed after that point.
        onFailure();
        return;
      }
      if (!getRefreshToken()) {
        return;
      }
      if (!exp || exp - Date.now() <= SKEW_MS) {
        runRefresh();
      }
    };

    // Both tokens get the same treatment on tab return / focus / reconnect.
    const onWake = () => {
      refreshIfStale();
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        onWake();
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);

    const expMs = getAccessTokenExpiryMs();
    if (getToken() && expMs !== null && expMs <= Date.now() && !getRefreshToken()) {
      onFailure();
    } else if (getRefreshToken() && (!expMs || expMs - Date.now() <= SKEW_MS)) {
      // Access token already gone/near-gone but we can still refresh.
      runRefresh();
    } else {
      schedule();
    }

    return () => {
      cancelled = true;
      clearTimer();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
    };
  }, [dispatch]);

  return null;
}
